import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { CartItems } from './components/CartItems';
import { OrderSummary } from './components/OrderSummary';
import { OrderTotal } from './components/OrderTotal';
import { CartCTA } from './components/CartCTA';
import { DrawerToggleState } from '../models/actionStates';
import { Spinner } from '../root/components/Spinner';
import { isInHolidayShippingRange, shippingHelpers, ShippingMethod } from '../checkout/shipping-methods';
import { isAfterToday, parseStringAsAmericaLosAngelesTimezone, uniqueArray } from '../util';
import { discountForDisplay } from '../discounts';
import { useRecoilState, useRecoilValue } from 'recoil';
import { abTestState, assetPathFnState, shopifyState, toViewPriceFnState, environmentContextState } from '../recoil/atoms';
import { cartDrawerState, cartState, isMobileState, productsState, productListState } from '../recoil/selectors';
import { CartSubHeader } from './components/CartSubHeader';
import { useSharedGetPreorderState } from '../hooks/actions/usePreorderState';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { trackLineItemListViewed } from '../tracking/events';
import { noReturnLineItemPropertyKey } from '../hooks/actions/useCartState';
import { CartBottomMessage } from './components/CartBottomMessage';
import { CartPromoList } from './components/CartPromoList';
import styles from './CartDrawer.module.scss';
import { CardFooter } from '../components/CardFooter';
import vhCheck from 'vh-check';
import { useOnScreen } from '../hooks/useOnScreen';
import { ShopifyLineItem } from '../models/ShopifyLineItem.model';
declare const window: HoneyloveWindow;

const CartDrawer: React.FC = () => {
  const cart = useRecoilValue(cartState);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const isMobile = useRecoilValue(isMobileState);
  const shopify = useRecoilValue(shopifyState);
  const abTests = useRecoilValue(abTestState);
  const preorders = useSharedGetPreorderState();
  const [cartDrawer, setCartDrawer] = useRecoilState(cartDrawerState);
  const cartDrawerWrapRef = useRef<HTMLDivElement>(null);
  const lineItemsRef = useRef<HTMLDivElement>(null);
  const lineItemsTopOnScreen = useOnScreen(lineItemsRef);
  const lineItemsAreScrolling = lineItemsRef?.current && lineItemsTopOnScreen === false;
  // this calculates the offsets necessary for bottom positioning of
  // buttons on the iphone. The Library imported here also provides
  // the css variable --vh-offset
  const vhIphoneAdjustment: {
    isNeeded: boolean;
    value: string | number;
    vh: number;
    windowHeight: number;
    offset: number;
    recompute: () => void;
    unbind: () => void;
  } = vhCheck();

  const isUSD = shopify?.currency.active === 'USD';
  const hasPreorders = !!preorders.find(
    (v) => isAfterToday(parseStringAsAmericaLosAngelesTimezone(String(v.date))) && (cart?.lineItems ?? []).map((z: any) => z.sku).includes(v.sku),
  );
  const total = cart?.totalPrice ?? 0;
  const showOrderNow = !hasPreorders && isUSD && total > 75 && !isInHolidayShippingRange();
  const showHeader = !showOrderNow && isUSD;
  const hasLineItems = !!cart?.lineItems?.length;
  const products = useRecoilValue(productsState);
  const hasFinalSaleItems = !!cart?.lineItems?.find((li) => {
    const product = li.handle && products[li.handle];
    return li.properties[noReturnLineItemPropertyKey] || !product?.isReturnable;
  });
  const productHandlesInCart = uniqueArray((cart?.lineItems ?? []).map((li) => li.handle));
  const productList = useRecoilValue(productListState);
  const colorPromoProduct = productList.find((p) => productHandlesInCart.includes(p.handle));
  const { timedSaleActive } = useRecoilValue(environmentContextState);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const displayDiscount = discountForDisplay(2, toViewPriceFn, timedSaleActive, shopify, abTests);

  const ab2022_09Active = abTests['AB2022-09'] && colorPromoProduct && colorPromoProduct.colors.length > 1 && productHandlesInCart.length === 1;

  useEffect(() => {
    if (cartDrawer.toggleState === DrawerToggleState.Open && cart?.itemCount) {
      trackLineItemListViewed(window.Shopify, cart?.lineItems ?? [], 'Cart Drawer');
    }
  }, [cartDrawer.toggleState]);

  useEffect(() => {
    if (cartDrawer.toggleState === DrawerToggleState.Open && cartDrawerWrapRef.current) {
      cartDrawerWrapRef.current.scrollTop = 0;
    }
  }, [cartDrawer.toggleState, cart?.itemCount]);

  const consolidatedItems = (cart.lineItems ?? []).reduce<ShopifyLineItem[]>((acc, curr) => {
    if (
      acc.some(
        (li) =>
          curr.properties?._bundleHandle &&
          li.properties?._bundleHandle === curr.properties._bundleHandle &&
          li.properties?._bundleKey === curr.properties._bundleKey,
      )
    )
      return acc;
    return [...acc, curr];
  }, []);
  const itemCount = consolidatedItems.length;
  const addMoreMarginOffset = shopify?.currency.active !== 'USD';

  return (
    <>
      {showHeader && <div className={styles.hlCartDrawerSubheader}>~ Free returns for 30 days ~</div>}
      {showOrderNow && (
        <div
          className={classNames(styles.hlCartDrawerSubheader, styles.orderNow)}
          dangerouslySetInnerHTML={{ __html: shippingHelpers.orderNowMessage(ShippingMethod.Priority) }}
        />
      )}
      <div className={classNames(styles.cartSectionWrapper)}>
        <div className={styles.mainCart}>
          <div className={classNames(styles.hlCartDrawerHeader, { [styles.contentsScrolling]: lineItemsAreScrolling })}>
            <button onClick={() => setCartDrawer({ toggleState: DrawerToggleState.Closed })} className={classNames(styles.hlCartDrawerControl, styles.arrow)}>
              <img src={assetPathFn('v2-left-arrow.png')} alt="left arrow" />
            </button>
            <button onClick={() => setCartDrawer({ toggleState: DrawerToggleState.Closed })} className={classNames(styles.hlCartDrawerControl, styles.close)}>
              <img src={assetPathFn('close.svg')} alt="close" />
            </button>
            <div className={styles.hlCartDrawerIcon}>
              <img src={assetPathFn('bag-icon.svg')} alt="bag icon" />
              {itemCount > 0 && <span className={styles.cartCount}>{itemCount}</span>}
            </div>
            <div className={styles.hlCartDrawerControl} />
          </div>
          <div id="hl-cart-drawer-scrollable" className={styles.hlCartDrawerWrap} ref={cartDrawerWrapRef}>
            <div ref={lineItemsRef} style={{ height: 1 }} />
            <CartSubHeader cart={cart} />
            {ab2022_09Active && (
              <div className={styles.greyPromoBackground}>
                <CartPromoList cart={cart} title={`Get ${displayDiscount} off another color`} />
              </div>
            )}
            <CartItems lineItems={cart?.lineItems} />
            {isMobile ? (
              <div className={classNames(styles.cartPromo)} style={addMoreMarginOffset ? { marginBottom: 100 } : {}}>
                <CardFooter inlineStyles={{ marginBottom: '10px' }} />
                {!ab2022_09Active && <CartPromoList cart={cart} />}
              </div>
            ) : (
              <OrderSummary cart={cart} />
            )}
          </div>
          <div className={classNames(styles.hlCartDrawerFooter, { [styles.iphoneShift]: vhIphoneAdjustment.isNeeded })}>
            <OrderTotal cart={cart} />
            <CartCTA hasLineItems={hasLineItems} token={cart?.token} />
            <CartBottomMessage hasFinalSaleItems={hasFinalSaleItems} />
          </div>
          <div className={classNames(styles.loadingContainer, { hidden: !cart.loading })}>
            <Spinner visible={!!cart.loading} />
          </div>
        </div>
        {!isMobile && (
          <div className={styles.cartPromo}>
            <CartPromoList cart={cart} forceItemUpsell={ab2022_09Active} />
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
